import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Lock, EnvelopeSimple, GraduationCap, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const HERO_IMG =
  "https://images.unsplash.com/photo-1775369406135-4f2540f29d0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxzY2hvb2wlMjBidWlsZGluZyUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzgzMTUzOTZ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user.name}`);
      navigate(from, { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="login-page">
      {/* Left: form */}
      <div className="flex items-center justify-center px-6 py-10 sm:px-10">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-2.5 mb-10">
            <div className="h-10 w-10 rounded-md bg-blue-900 text-white flex items-center justify-center">
              <GraduationCap size={22} weight="duotone" />
            </div>
            <div>
              <div className="text-base font-bold tracking-tight text-slate-900 font-heading">Fee ERP</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Multi-School SaaS</div>
            </div>
          </Link>

          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 font-heading">
            Welcome back
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Sign in to manage schools, fees and student records.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1.5 block">
                Email
              </Label>
              <div className="relative">
                <EnvelopeSimple
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  id="email"
                  type="email"
                  required
                  data-testid="login-email-input"
                  placeholder="you@school.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-blue-900 hover:underline font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  required
                  data-testid="login-password-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-11"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-blue-900 hover:bg-blue-800 gap-2 text-sm font-medium"
            >
              {submitting ? "Signing in…" : "Sign in"}
              {!submitting && <ArrowRight size={16} weight="bold" />}
            </Button>
          </form>

          <p className="mt-8 text-xs text-slate-500">
            Need a school onboarded? Contact your Super Administrator.
          </p>
        </div>
      </div>

      {/* Right: brand panel */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO_IMG} alt="School" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/85 via-blue-900/75 to-slate-900/80" />
        <div className="relative h-full flex flex-col justify-end p-10 text-white">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold opacity-80 mb-3">
            Built for Indian schools
          </div>
          <h2 className="text-3xl xl:text-4xl font-bold tracking-tight max-w-md font-heading leading-tight">
            One platform for every fee, every receipt, every school.
          </h2>
          <p className="mt-4 text-sm xl:text-base text-blue-100 max-w-md">
            Multi-tenant, role-aware, audit-tracked. Designed for daily classroom use and
            district-level oversight.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
            <div>
              <div className="text-2xl font-bold font-heading">100%</div>
              <div className="text-xs text-blue-100 mt-1">Tenant Isolation</div>
            </div>
            <div>
              <div className="text-2xl font-bold font-heading">3</div>
              <div className="text-xs text-blue-100 mt-1">Role tiers</div>
            </div>
            <div>
              <div className="text-2xl font-bold font-heading">∞</div>
              <div className="text-xs text-blue-100 mt-1">Schools onboardable</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
